/**
 * WhoIsItFor — persona-based curiosity hook for the landing page.
 *
 * The user said: "sodass jemand der keine ahnung hat lust bekommt mehr
 * zu erfahren". The trick is for newcomers to immediately see themselves
 * in one of these 5 cards — then they know exactly which feature to
 * click on next.
 *
 * Visual rhythm: bold persona icon, "Du bist…"-eyebrow in their tone
 * colour, 1-sentence pain point, 3 specific Bodenblick pages with
 * one-liner explanations. Big tap-targets, no skim-skipping.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Tractor, GraduationCap, Flower, ChartLineUp,
  ArrowRight, Sparkle,
} from "@phosphor-icons/react";

const PERSONAS = [
  {
    id: "buerger",
    Icon: Heart,
    eyebrow: "Du bist neugierig",
    color: "#00FA9A",
    bg: "linear-gradient(160deg, #0E1A14 0%, #FFFFFF 80%)",
    pain: `„Klimawandel? Ich seh's im Fernsehen — aber was passiert HIER, in meiner Region, gerade jetzt?"`,
    answer: "Bodenblick zeigt dir Live-Daten für 42 Regionen in DE/AT — Vegetation, Wetter, Bestäuber, Waldbrände. Alles ohne Anmeldung.",
    links: [
      { to: "/dashboard", label: "Daten-Cockpit", desc: "Alle Live-Werte auf einen Blick" },
      { to: "/heritage", label: "Opas Wetter", desc: "Wie war's vor 30 Jahren bei dir?" },
      { to: "/heute", label: "Heute lesen", desc: "Eine neue Klima-Geschichte pro Tag" },
    ],
  },
  {
    id: "landwirt",
    Icon: Tractor,
    eyebrow: "Du bist Landwirt:in",
    color: "#D9A87C",
    bg: "linear-gradient(160deg, #16130D 0%, #FFFFFF 80%)",
    pain: `„Ich brauch handfeste Zahlen für meine Aussaat-, Bewässerungs- und Pflanzenschutz-Entscheidungen."`,
    answer: "Eigene Felder einzeichnen, Bodenfeuchte, NDVI-Trend, Krankheits-Druck, Bestäuber-Wetter — alles für deinen konkreten Schlag.",
    links: [
      { to: "/myfields", label: "Mein Hof", desc: "Eigene Felder zeichnen + analysieren" },
      { to: "/diseases", label: "Krankheitswarner", desc: "Mehltau · Rost · Septoria-Risiko" },
      { to: "/commodities", label: "Markt-Cockpit", desc: "Weizen · Mais · Raps · Soja Preise" },
    ],
  },
  {
    id: "imker",
    Icon: Flower,
    eyebrow: "Du bist Imker:in",
    color: "#FFC24B",
    bg: "linear-gradient(160deg, #241C06 0%, #FFFFFF 80%)",
    pain: `„Wann fliegt mein Volk? Wann setze ich die Honigräume auf?"`,
    answer: "Bienen-Wetter (≥ 12 °C-Schwelle), Blüh-Kalender mit 8 Trachten, jeder Pflanze mit Honigsorte und Bestäuber-Liste.",
    links: [
      { to: "/bees", label: "Bienen-Kalender", desc: "Was blüht jetzt? Welche Honigsorte?" },
      { to: "/dashboard", label: "Bestäuber-Tag", desc: "Aktuelle Flug-Bedingungen" },
      { to: "/feldfruechte", label: "Feldfrüchte", desc: "Welche Tracht erkennst du?" },
    ],
  },
  {
    id: "schule",
    Icon: GraduationCap,
    eyebrow: "Du bist Lehrkraft / Schüler:in",
    color: "#3D7A8C",
    bg: "linear-gradient(160deg, rgba(77,163,255,0.10) 0%, #FFFFFF 80%)",
    pain: `„Wir brauchen echte Daten für den Unterricht — keine Lehrbuch-Mittelwerte aus 1995."`,
    answer: "Schulgarten als Polygon zeichnen, Bodenfeuchte + NDVI tracken, tägliches Foto-Tagebuch führen, Klassen-Wrapped als Jahres-Abschluss.",
    links: [
      { to: "/schulgarten", label: "Unser Schulgarten", desc: "Polygon + Live-Daten + Foto-Tagebuch" },
      { to: "/schulgaerten", label: "Andere Schulen", desc: "Sieh, was andere Klassen tun" },
      { to: "/glossar", label: "Glossar", desc: "Fachbegriffe einfach erklärt" },
    ],
  },
  {
    id: "markt",
    Icon: ChartLineUp,
    eyebrow: "Du willst Märkte verstehen",
    color: "#FF5500",
    bg: "linear-gradient(160deg, rgba(255,85,0,0.10) 0%, #FFFFFF 80%)",
    pain: `„Warum steigt Weizen? Was hat die Trockenheit in Russland mit den Linzer Preisen zu tun?"`,
    answer: "CBOT-, MATIF- und lokale AMA/AMI-Daten verbunden mit den Klima- und Vegetations-Anomalien — Ursache & Wirkung in einer Ansicht.",
    links: [
      { to: "/commodities", label: "Rohstoff-Preise", desc: "Live-Charts · 30-Tage-Anomalie" },
      { to: "/correlation", label: "Korrelation", desc: "NDVI ↔ Preis sichtbar machen" },
      { to: "/markt-akademie", label: "Markt-Akademie", desc: "Markt verstehen ohne Studium" },
    ],
  },
];

function PersonaCard({ p, idx }) {
  const Icon = p.Icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.45, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="border border-white/15 overflow-hidden flex flex-col"
      style={{ background: p.bg }}
      data-testid={`persona-${p.id}`}
    >
      <div className="p-5 sm:p-6 flex-1">
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-sm flex items-center justify-center shrink-0"
            style={{ background: p.color, color: "#FFFFFF" }}
            aria-hidden="true"
          >
            <Icon size={26} weight="fill" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-mono font-semibold" style={{ color: p.color }}>
              {p.eyebrow}
            </div>
            <p className="font-display text-base sm:text-lg font-bold tracking-tight leading-snug mt-1 italic text-white/90">
              {p.pain}
            </p>
          </div>
        </div>
        <p className="text-sm text-white/80 mt-4 leading-relaxed">
          {p.answer}
        </p>
      </div>
      <ul className="border-t border-white/15 divide-y divide-white/15 bg-black/40">
        {p.links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="px-5 py-3 flex items-center gap-3 hover:bg-[#121212] transition-colors group"
              data-testid={`persona-${p.id}-link-${l.to.replace("/", "")}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-white group-hover:underline" style={{ textDecorationColor: p.color }}>
                  {l.label}
                </div>
                <div className="text-xs text-white/65 mt-0.5">{l.desc}</div>
              </div>
              <ArrowRight size={14} weight="bold" style={{ color: p.color }} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

export default function WhoIsItFor() {
  return (
    <section data-testid="who-is-it-for" className="space-y-5">
      <div>
        <div className="eid-label inline-flex items-center gap-1.5 text-[#D9A87C]">
          <Sparkle size={11} weight="fill" /> Für wen ist Bodenblick?
        </div>
        <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-1.5 leading-[0.9] max-w-3xl">
          Finde dich in 5 Sekunden wieder.
        </h2>
        <p className="text-sm sm:text-base text-white/70 mt-3 max-w-2xl leading-relaxed">
          Egal ob du nur neugierig bist, einen Hof bewirtschaftest oder im Klassen-Garten
          tüftelst — wir haben die richtige Tür für dich. Klick einfach drauf.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {PERSONAS.map((p, i) => <PersonaCard p={p} idx={i} key={p.id} />)}
      </div>
    </section>
  );
}
